import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { INR, systemCostForKw } from '@/lib/utils';
import { IndianRupee, Wallet, Calendar } from 'lucide-react';

export function EmiCalculator({ initialCapacityKw = 3, showBookingCTA = true, onBook }) {
    const [cfg, setCfg] = useState({ default_interest_rate: 9.5, default_tenure_months: 60, tenure_options: [12,24,36,48,60,84,120], booking_amount: 1 });
    const [capacity, setCapacity] = useState([initialCapacityKw]);
    const [rates, setRates] = useState([]);
    const [interest, setInterest] = useState(9.5);
    const [tenure, setTenure] = useState(60);

    useEffect(() => {
        api.emiConfig().then(c => { setCfg(c); setInterest(c.default_interest_rate); setTenure(c.default_tenure_months); });
        api.subsidyRates().then(setRates);
    }, []);

    // Prefer admin's rate chart (has slab-wise system_cost); fallback to shared util.
    const systemCost = useMemo(() => {
        if (rates.length) {
            const sorted = [...rates].sort((a, b) => a.kw - b.kw);
            let picked = sorted[0];
            for (const r of sorted) if (r.kw <= capacity[0]) picked = r;
            if (picked?.system_cost && picked.system_cost > 0) {
                // Linear-extend for higher capacities (e.g. slider at 4kW with slab at 3kW → scale by kw ratio)
                if (picked.kw === capacity[0]) return picked.system_cost;
                return Math.round((picked.system_cost / picked.kw) * capacity[0]);
            }
        }
        return systemCostForKw(capacity[0]);
    }, [rates, capacity]);
    const subsidyMax = useMemo(() => {
        if (!rates.length) return 108000;
        const sorted = [...rates].sort((a, b) => a.kw - b.kw);
        let picked = sorted[0];
        for (const r of sorted) if (r.kw <= capacity[0]) picked = r;
        return picked.central_subsidy + picked.state_subsidy;
    }, [rates, capacity]);

    const netFinance = Math.max(0, systemCost - subsidyMax - cfg.booking_amount);
    const monthlyRate = (Number(interest) / 100) / 12;
    const emi = monthlyRate > 0 && netFinance > 0
        ? (netFinance * monthlyRate * Math.pow(1 + monthlyRate, tenure)) / (Math.pow(1 + monthlyRate, tenure) - 1)
        : (netFinance / tenure);
    const totalPayable = Math.round((emi * tenure) + Number(cfg.booking_amount));

    return (
        <Card className='card-elevate border-accent/30' data-testid='emi-calculator'>
            <CardHeader className='pb-2 pt-3 sm:pt-4'>
                <div className='flex items-center gap-2'>
                    <Wallet className='w-4 h-4 text-accent' />
                    <CardTitle className='text-base sm:text-lg'>Easy EMI पर Solar</CardTitle>
                    <Badge className='ml-auto bg-accent/15 text-accent border-accent/30 text-[10px] sm:text-xs'>मात्र ₹1 booking</Badge>
                </div>
                <div className='text-[11px] sm:text-xs text-muted-foreground'>Rate chart aur subsidy ke saath monthly EMI calculate karein</div>
            </CardHeader>
            <CardContent className='space-y-3 sm:space-y-4'>
                <div>
                    <div className='flex items-baseline justify-between'>
                        <Label>System Capacity</Label>
                        <div className='font-display text-lg sm:text-xl font-semibold'>{capacity[0]} kW</div>
                    </div>
                    <Slider min={1} max={10} step={0.5} value={capacity} onValueChange={setCapacity} data-testid='emi-capacity-slider' />
                </div>
                <div className='grid grid-cols-2 gap-3'>
                    <div className='space-y-1'>
                        <Label>Interest Rate (% p.a.)</Label>
                        <Input type='number' step='0.1' value={interest} onChange={e => setInterest(e.target.value)} className='min-h-[40px] sm:min-h-[44px]' data-testid='emi-interest-input' />
                    </div>
                    <div className='space-y-1'>
                        <Label>Tenure (months)</Label>
                        <select className='w-full border rounded-md h-10 sm:h-11 px-3 bg-background text-sm' value={tenure} onChange={e => setTenure(Number(e.target.value))} data-testid='emi-tenure-select'>
                            {(cfg.tenure_options || [12,24,36,48,60]).map(t => <option key={t} value={t}>{t} months ({(t/12).toFixed(1)} years)</option>)}
                        </select>
                    </div>
                </div>
                <div className='rounded-xl bg-secondary/60 p-3 sm:p-4 space-y-1.5 sm:space-y-2 text-sm'>
                    <Row k='System Cost (approx)' v={INR(systemCost)} />
                    <Row k='Government Subsidy' v={<span className='text-accent font-medium'>-{INR(subsidyMax)}</span>} />
                    <Row k='Booking Amount' v={<>-{INR(cfg.booking_amount)} <Badge variant='outline' className='ml-1 text-[10px]'>Aaj bharein</Badge></>} />
                    <div className='border-t pt-1.5 sm:pt-2'>
                        <Row k='Net Loan Amount' v={<b>{INR(netFinance)}</b>} />
                    </div>
                    <div className='rounded-lg bg-accent/10 border border-accent/30 p-2.5 sm:p-3 mt-1.5 sm:mt-2 flex items-center justify-between'>
                        <div>
                            <div className='text-[10px] sm:text-xs text-muted-foreground'>Monthly EMI</div>
                            <div className='font-display text-xl sm:text-2xl font-semibold text-accent' data-testid='emi-monthly-value'>{INR(emi)}</div>
                        </div>
                        <div className='text-right text-[10px] sm:text-xs text-muted-foreground'>
                            <div>Total Payable</div>
                            <div className='font-medium text-foreground'>{INR(totalPayable)}</div>
                        </div>
                    </div>
                </div>
                {showBookingCTA && (
                    <button
                        className='w-full bg-accent hover:bg-accent/90 text-accent-foreground rounded-lg py-2.5 sm:py-3 font-medium'
                        onClick={() => onBook ? onBook({ capacity: capacity[0], emi, tenure, interest }) : window.location.assign('/customer/apply')}
                        data-testid='emi-book-now-button'>
                        ₹1 Booking Confirm karein / Apply
                    </button>
                )}
                <div className='text-[10px] sm:text-[11px] text-muted-foreground'>*EMI illustrative. Final terms subject to bank approval. Interest & rates configurable by admin.</div>
            </CardContent>
        </Card>
    );
}
function Row({ k, v }) { return (<div className='flex items-center justify-between'><span className='text-muted-foreground'>{k}</span><span>{v}</span></div>); }
export default EmiCalculator;
